import actions from '@src/redux/actions';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { audioExpired } from '@src/services/project';
import { BLOCK_STATUS, CONVERTING_STATUS } from '@src/constants/project';
import { convertSubtitleTimeToSeconds } from '@src/utils/time';
import { findSlotAtSeconds } from '@src/services/dubbing';
import { useBlockAudioPreview } from './useBlockAudioPreview';
import { useVoiceClipPlayer } from './useVoiceClipPlayer';

// How often the virtual clock advances (ms). 100ms is smooth enough for the
// seekbar without re-rendering the (large) content tree every animation frame.
const CLOCK_INTERVAL_MS = 100;
// Re-seek a follower only when it drifts more than this from the timeline.
const DRIFT_THRESHOLD_SECONDS = 0.3;

/**
 * The next block the clock will reach that has audio — used to prefetch its file
 * into the browser cache so entering it pays no network latency.
 *
 * `audioBlocks` is pre-filtered to blocks WITH audio (still in ascending startSec
 * order), so this is a plain upper-bound binary search for the first one
 * starting strictly after the clock: O(log n) with no linear scan, cheap on
 * every tick even for a few thousand blocks.
 */
const findNextAudioBlock = (audioBlocks, timelineSeconds) => {
  let low = 0;
  let high = audioBlocks.length;
  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    if (audioBlocks[mid].startSec > timelineSeconds) high = mid;
    else low = mid + 1;
  }
  return audioBlocks[low] ?? null;
};

// Nudge a media element to `targetSec`, but only once it has drifted past the
// threshold — re-seeking every tick would stutter playback.
const seekIfDrifted = (media, targetSec) => {
  if (Math.abs(media.currentTime - targetSec) > DRIFT_THRESHOLD_SECONDS) {
    // eslint-disable-next-line no-param-reassign
    media.currentTime = targetSec;
  }
};

// Resume a paused element only while the timeline is playing. Skip an ended
// clip — calling play() on it would restart it from 0.
const resumeIfPlaying = (media, isPlaying) => {
  if (isPlaying && media.paused && !media.ended) media.play().catch(() => {});
};

const ensurePaused = (media) => {
  if (!media.paused) media.pause();
};

export const useVideoDubbingAudioPlayer = ({
  enabled = false,
  videoRef = null,
  // The caller reads the moving time itself via `getCurrentSec`, so don't
  // publish `currentTime` while playing — playback then re-renders nothing. It
  // is still published on pause / seek / reset. Callers that render the time
  // from state (a fine-grained seekbar) must leave this off.
  liveTimeOnly = false,
} = {}) => {
  const dispatch = useDispatch();

  const { isPlaying, multiBlock, currentBlockId, currentFilteringStatus } =
    useSelector((state) => state.project);
  const voiceVolume = useSelector((state) => state.project.volumes?.voiceover);
  const musicClips = useSelector((state) => state.project.musicClips);
  const { blocks, blockIds } = multiBlock || {};

  // Voice playback (loading, cutting and stopping a block's clip) lives in its
  // own engine; this hook owns the clock and the followers that chase it. Each
  // callback is destructured rather than kept as one object: they sit in
  // syncMedia's deps, and syncMedia sits in the clock effect's, so a new
  // identity would restart the interval on every render.
  const {
    elementRef: voiceElementRef,
    enterBlock: enterVoiceBlock,
    leaveBlock: leaveVoiceBlock,
    stopAll: stopVoice,
    pauseAll: pauseVoice,
    setVolume: setVoiceVolume,
    prefetch: prefetchVoiceClip,
  } = useVoiceClipPlayer({ enabled });
  // Last link handed to the prefetcher, so warming the next clip fires on a
  // block change rather than on every 100ms tick.
  const prefetchedLinkRef = useRef(null);

  const [displayTime, setDisplayTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  // Playhead past the last frame of the video — the preview paints the project
  // background for that stretch. Published from syncMedia, which already
  // decides it to park the video, and only on a change: the clock ticks ten
  // times a second and must not re-render the editor that often.
  const [isBeyondVideo, setIsBeyondVideo] = useState(false);
  const isBeyondVideoRef = useRef(false);
  const timelineTimeRef = useRef(0);
  const clockIntervalRef = useRef(null);
  const lastTickRef = useRef(0);

  const isPlayingRef = useRef(isPlaying);
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  const currentBlockIdRef = useRef(currentBlockId);
  useEffect(() => {
    currentBlockIdRef.current = currentBlockId;
  }, [currentBlockId]);

  // ALL blocks ordered on the timeline (focus follows position even for blocks
  // without audio); `audioLink` is null when the block has no valid audio.
  const timelineBlocks = useMemo(() => {
    if (!enabled || !blocks || !blockIds) return [];
    return blockIds.map((blockId) => {
      const block = blocks[blockId];
      const startSec = block?.startTime
        ? convertSubtitleTimeToSeconds(block.startTime)
        : 0;
      const endSec = block?.endTime
        ? convertSubtitleTimeToSeconds(block.endTime)
        : startSec;
      // "Playable" mirrors the block editor's own play gate exactly
      // (Dubbing/Content/ContentBlock `isDisablePlayAudio` = changed ||
      // !successSynthesized || expired). `blockStatus === AUDIO_AVAILABLE` is
      // precisely that: success-synthesized, NOT changed (an edited block is
      // DRAFT), not error/in-progress, not expired — and it's kept fresh via
      // updateBlockStatus on every edit. So full-project playback skips the same
      // blocks the block won't play, including ones edited after synthesis.
      // (`audioExpired` re-checked live too: a block can expire mid-session
      // after its status was computed.)
      const hasValidAudio =
        block?.blockStatus === BLOCK_STATUS.AUDIO_AVAILABLE &&
        block?.audioLink &&
        block.audioLink.trim() !== '' &&
        !audioExpired(block?.audioExpiredAt);
      return {
        blockId,
        startSec,
        endSec: Math.max(startSec, endSec),
        audioLink: hasValidAudio ? block.audioLink : null,
        // Its audio is being generated right now. Carried here (rather than read
        // from `blocks` at play time) so syncMedia can stay on refs and keep its
        // identity — it sits in the clock effect's deps, and a new identity
        // restarts the interval on every keystroke in any block.
        isConverting: block?.status === CONVERTING_STATUS.IN_PROGRESS,
      };
    });
  }, [enabled, blocks, blockIds]);

  const lastEndSec = useMemo(
    () =>
      timelineBlocks.reduce(
        (latest, block) => Math.max(latest, block.endSec),
        0,
      ),
    [timelineBlocks],
  );
  // The BGM can outlast both the video and the last block, and playback has to
  // reach its end too — past the video the preview shows the project background
  // instead of a frame, but the audio keeps running.
  const musicEndSec = useMemo(
    () =>
      (musicClips || []).reduce(
        (latest, clip) => Math.max(latest, clip?.endSec || 0),
        0,
      ),
    [musicClips],
  );
  // total = MAX(last block endTime, video duration, last bgm clip end).
  const totalDuration = Math.max(lastEndSec, videoDuration || 0, musicEndSec);

  const timelineBlocksRef = useRef(timelineBlocks);
  useEffect(() => {
    timelineBlocksRef.current = timelineBlocks;
  }, [timelineBlocks]);

  // Blocks WITH audio only (same order) — the search space for prefetching the
  // next clip. Built once per data change so the per-tick lookup stays O(log n)
  // with no scan over audio-less blocks, even at a few thousand blocks.
  const audioBlocks = useMemo(
    () => timelineBlocks.filter((block) => block.audioLink),
    [timelineBlocks],
  );
  const audioBlocksRef = useRef(audioBlocks);
  useEffect(() => {
    audioBlocksRef.current = audioBlocks;
  }, [audioBlocks]);
  const totalRef = useRef(totalDuration);
  useEffect(() => {
    totalRef.current = totalDuration;
  }, [totalDuration]);

  // Track the video duration once its metadata is available.
  useEffect(() => {
    if (!enabled) return undefined;
    const video = videoRef?.current;
    if (!video) return undefined;
    const onLoaded = () => {
      if (video.duration && !Number.isNaN(video.duration)) {
        setVideoDuration(video.duration);
      }
    };
    if (video.duration && !Number.isNaN(video.duration)) onLoaded();
    video.addEventListener('loadedmetadata', onLoaded);
    return () => video.removeEventListener('loadedmetadata', onLoaded);
  }, [enabled, videoRef]);

  const findActiveBlock = useCallback(
    (timelineSeconds) =>
      findSlotAtSeconds(timelineBlocksRef.current, timelineSeconds),
    [],
  );

  const findNextAudio = useCallback(
    (timelineSeconds) =>
      findNextAudioBlock(audioBlocksRef.current, timelineSeconds),
    [],
  );

  // Prepare the clips around `timelineSeconds` — the one under the playhead and
  // the one after it. Pressing play, or seeking then playing, must not wait on a
  // fetch+decode: a clip that arrives late starts late, and its tail then runs
  // past the block into the next one.
  const warmClipsAt = useCallback(
    (timelineSeconds) => {
      [findActiveBlock(timelineSeconds), findNextAudio(timelineSeconds)]
        .filter((block) => block?.audioLink)
        .forEach((block) =>
          prefetchVoiceClip(block.audioLink, block.endSec - block.startSec),
        );
    },
    [findActiveBlock, findNextAudio, prefetchVoiceClip],
  );

  // Warm the start of the project (or wherever the playhead sits) as soon as the
  // block list is known, so the very first block is ready before play is pressed.
  useEffect(() => {
    if (!enabled || !audioBlocks.length) return;
    warmClipsAt(timelineTimeRef.current);
  }, [enabled, audioBlocks, warmClipsAt]);

  const publishBeyondVideo = useCallback((value) => {
    if (isBeyondVideoRef.current === value) return;
    isBeyondVideoRef.current = value;
    setIsBeyondVideo(value);
  }, []);

  // Align both followers (video + audio) and block focus to `timelineSeconds`.
  // `forceAudioSeek` is set only for intentional jumps (user seek / block-focus
  // click) — the periodic clock ticks never reposition the audio, so a block's
  // clip always plays from its start instead of skipping ahead to "catch up".
  const syncMedia = useCallback(
    (timelineSeconds, { forceAudioSeek = false } = {}) => {
      const playing = isPlayingRef.current;

      // Video follows the clock until its own end, then freezes on the last
      // frame (the timeline can outlast it when the audio is longer).
      const video = videoRef?.current;
      if (video) {
        const withinVideo =
          !video.duration || timelineSeconds <= video.duration;
        if (withinVideo) {
          seekIfDrifted(video, timelineSeconds);
          resumeIfPlaying(video, playing);
        } else {
          ensurePaused(video);
        }
        // An unknown duration counts as within: there is no known end to be
        // past, so the preview keeps showing the video.
        publishBeyondVideo(!withinVideo);
      }

      // Focus the block whose slot contains the clock (null while in a gap).
      // A block still generating its audio is skipped: its clip is inert on the
      // track (spinner, no selection, no trim), so focusing it would highlight
      // something the user cannot act on.
      const activeBlock = findActiveBlock(timelineSeconds);
      const activeBlockId =
        activeBlock && !activeBlock.isConverting ? activeBlock.blockId : null;
      if (activeBlockId !== currentBlockIdRef.current) {
        dispatch(actions.project.setCurrentBlockId(activeBlockId));
      }

      // Prepare the NEXT block's clip (fetch + decode + cut) ahead of time, so
      // reaching that block costs only a `src` swap to an already-built blob.
      const nextAudioBlock = findNextAudio(timelineSeconds);
      if (
        nextAudioBlock &&
        prefetchedLinkRef.current !== nextAudioBlock.audioLink
      ) {
        prefetchedLinkRef.current = nextAudioBlock.audioLink;
        prefetchVoiceClip(
          nextAudioBlock.audioLink,
          nextAudioBlock.endSec - nextAudioBlock.startSec,
        );
      }

      // The clock is past every block with audio — in a gap or on a block with
      // none. Rolling out of a block does NOT cut its clip short: the clip
      // already carries its own exact end, and stopping it on the 100ms clock is
      // precisely what used to swallow the last words of one that started a
      // moment late. A jump lands somewhere the user chose, so anything still
      // sounding from where they were must stop.
      if (!activeBlock?.audioLink) {
        if (forceAudioSeek) stopVoice();
        else leaveVoiceBlock();
        return;
      }

      // A block the clock simply reached starts at its FIRST sample. The clock
      // ticks every 100ms, so it enters a block up to that late; seeking the
      // clip to that offset (as this used to) threw away the start of the very
      // first word. Only a deliberate seek lands inside a block.
      const offsetSec = forceAudioSeek
        ? Math.max(0, timelineSeconds - activeBlock.startSec)
        : 0;
      enterVoiceBlock({
        blockId: activeBlock.blockId,
        audioLink: activeBlock.audioLink,
        slotSec: activeBlock.endSec - activeBlock.startSec,
        offsetSec,
        playing,
        force: forceAudioSeek,
      });
    },
    [
      dispatch,
      findActiveBlock,
      findNextAudio,
      videoRef,
      publishBeyondVideo,
      enterVoiceBlock,
      leaveVoiceBlock,
      stopVoice,
      prefetchVoiceClip,
    ],
  );

  const stopPlayback = useCallback(() => {
    stopVoice();
    const video = videoRef?.current;
    if (video) video.pause();
    dispatch(actions.project.setIsPlaying(false));
    dispatch(actions.project.setCurrentBlockId(null));
    dispatch(actions.project.setIsMultiBlockMode(false));
    prefetchedLinkRef.current = null;
    timelineTimeRef.current = 0;
    setDisplayTime(0);
  }, [dispatch, videoRef, stopVoice]);

  // The virtual clock: advances by real elapsed time while playing, syncing the
  // followers each tick and ending playback once it reaches `total`.
  useEffect(() => {
    if (!enabled) return undefined;
    if (!isPlaying) {
      if (clockIntervalRef.current) {
        clearInterval(clockIntervalRef.current);
        clockIntervalRef.current = null;
      }
      // Land on the exact position the clock reached — under `liveTimeOnly`
      // nothing was published while playing. Publishing the same value again is
      // a no-op, so this is safe on every pass.
      setDisplayTime(timelineTimeRef.current);
      pauseVoice();
      const video = videoRef?.current;
      if (video) video.pause();
      return undefined;
    }
    lastTickRef.current = performance.now();
    syncMedia(timelineTimeRef.current);
    clockIntervalRef.current = setInterval(() => {
      const now = performance.now();
      const delta = (now - lastTickRef.current) / 1000;
      lastTickRef.current = now;
      let next = timelineTimeRef.current + delta;
      if (next >= totalRef.current) {
        next = totalRef.current;
        timelineTimeRef.current = next;
        setDisplayTime(next);
        stopPlayback();
        return;
      }
      timelineTimeRef.current = next;
      if (!liveTimeOnly) setDisplayTime(next);
      syncMedia(next);
    }, CLOCK_INTERVAL_MS);
    return () => {
      if (clockIntervalRef.current) {
        clearInterval(clockIntervalRef.current);
        clockIntervalRef.current = null;
      }
    };
  }, [
    enabled,
    isPlaying,
    syncMedia,
    stopPlayback,
    videoRef,
    liveTimeOnly,
    pauseVoice,
  ]);

  // Reset playback when the block-status filter changes.
  useEffect(() => {
    if (!enabled) return;
    stopVoice();
    const video = videoRef?.current;
    if (video) video.pause();
    timelineTimeRef.current = 0;
    setDisplayTime(0);
    dispatch(actions.project.setIsPlaying(false));
    dispatch(actions.project.setCurrentBlockId(null));
    dispatch(actions.project.setIsMultiBlockMode(false));
  }, [enabled, currentFilteringStatus, dispatch, videoRef, stopVoice]);

  const handlePlay = useCallback(() => {
    if (!timelineBlocks.length) return;
    // Restart from the beginning if the clock is already at the end.
    if (timelineTimeRef.current >= totalRef.current) {
      timelineTimeRef.current = 0;
      setDisplayTime(0);
    }
    dispatch(actions.project.setIsMultiBlockMode(true));
    dispatch(actions.project.setIsPlayingBlock(false));
    dispatch(actions.project.setIsPlaying(true));
  }, [timelineBlocks, dispatch]);

  const handlePause = useCallback(() => {
    dispatch(actions.project.setIsPlaying(false));
  }, [dispatch]);

  const handleSeeked = useCallback(
    (value) => {
      const clamped = Math.max(0, Math.min(value, totalRef.current));
      timelineTimeRef.current = clamped;
      setDisplayTime(clamped);
      // Force the followers (incl. audio) to the new position and refresh focus.
      syncMedia(clamped, { forceAudioSeek: true });
      warmClipsAt(clamped);
    },
    [syncMedia, warmClipsAt],
  );

  // Seek the timeline to the start of a block — used when the user clicks a
  // block to focus it, so the video/slider/audio jump to that block.
  const seekToBlock = useCallback(
    (blockId) => {
      const block = timelineBlocksRef.current.find(
        (b) => b.blockId === blockId,
      );
      if (!block) return;
      handleSeeked(block.startSec);
    },
    [handleSeeked],
  );

  // Stop the clock on unmount; the voice engine tears its own elements down.
  useEffect(
    () => () => {
      if (clockIntervalRef.current) clearInterval(clockIntervalRef.current);
    },
    [],
  );

  const blockPreview = useBlockAudioPreview({ enabled });

  const handleChangeVolume = useCallback(
    (value) => {
      setVoiceVolume(value / 100);
      blockPreview.setBlockVolume(value);
    },
    [blockPreview, setVoiceVolume],
  );

  // Voiceover level for timeline playback (the track's mute button). Held by the
  // voice engine and re-applied to whichever element takes over, so it survives
  // the per-block clip swaps.
  useEffect(() => {
    if (!enabled) return;
    setVoiceVolume(voiceVolume ?? 1);
  }, [enabled, voiceVolume, setVoiceVolume]);

  // Timeline position, read without a re-render. The clock only advances in
  // CLOCK_INTERVAL_MS steps, so while it is running we extrapolate from the last
  // tick — callers get a continuous position instead of visible steps.
  const getCurrentSec = useCallback(() => {
    const base = timelineTimeRef.current;
    if (!clockIntervalRef.current) return base;
    const next = base + (performance.now() - lastTickRef.current) / 1000;
    return totalRef.current > 0 ? Math.min(next, totalRef.current) : next;
  }, []);

  return {
    // The element currently owning voice playback. Only the legacy video-sync
    // path reads it, and that path is off whenever this engine is on.
    audioRef: voiceElementRef,
    handlePlay,
    handlePause,
    handleSeeked,
    seekToBlock,
    getCurrentSec,
    isLoading: false,
    isPlaying,
    currentTime: displayTime,
    duration: totalDuration,
    isBeyondVideo,
    handleChangeVolume,
    handlePlayAudioInBlock: blockPreview.handlePlayAudioInBlock,
    handleSeekedInBlock: blockPreview.handleSeekedInBlock,
    blockAudioCurrentTime: blockPreview.blockAudioCurrentTime,
    blockAudioDuration: blockPreview.blockAudioDuration,
  };
};

export default useVideoDubbingAudioPlayer;
